import * as _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { ODataOptions, ODataFilter, ODataFilterOperators, ODataOrderByDirection } from 'src/util/ODataBuilder';
import { ITableConfig } from 'src/components/common/table';
import useFetch from 'use-http';
import PBNavBar from '../PBNavBar';
import { useForm } from 'react-hook-form';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import TimeAndExpenseChecklistSearch, { ITimeAndExpenseChecklistSearchCriteria } from 'src/components/project-billing/components/TimeAndExpenseChecklistSearch';
import { MdAccessTime  } from 'react-icons/md';
import PayPeriod from 'src/models/AP/PayPeriod';
import TimeAndExpenseChecklist from 'src/models/projectSettings/TsdiTneChecklist';
import UpdateTimeAndExpenseChecklist from 'src/models/projectSettings/UpdateTeChecklistResponse';
import { FaSortAlphaDown, FaSortAlphaUp } from 'react-icons/fa';
import OfficeTimeAndExpenseEntries from 'src/models/AP/OfficeTimeAndExpenseEntries';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import actions from 'src/actions';
import DialogList from 'src/dialogs';
import ReceiptViewerDialog from 'src/components/client-project/dialogs/ReceiptViewerDialog';
import Receipt from 'src/components/project-billing/models/Receipt';
import DocumentSourceType from 'src/components/project-billing/models/DocumentSourceType';

const TimeAndExpenseChecklistPage = () => {
    const { register: registerOfficeTimeEntries, reset: resetOfficeTimeEntries, errors: officeTimeEntriesErrors } = useForm();
    const { register: registerTimeEntries, reset: resetTimeEntries, errors: timeEntriesErrors } = useForm();
    const { get, post, put, response } = useFetch();
    const [loadingTimeEntries, setLoadingTimeEntries] = useState<boolean>(true);
    const [loadingOfficeTimeEntrySummary, setLoadingOfficeTimeEntrySummary] = useState<boolean>(true);
    const [payPeriods, setPayPeriods] = useState<Array<PayPeriod>>(undefined);
    // Same checklist screen serves both Project Billing (Contractor landmen, 'C') and Employee
    // Payroll's Emp. Checklist button (Employee, 'E') - defaults to 'C' so direct Project Billing
    // navigation is unaffected; Employee Payroll sets backOfficeSelectedLmType before navigating
    // here, same pattern as backOfficeSelectedPayPeriod/backOfficeSelectedLandman elsewhere. Now
    // switchable in-page via the Employee Type radio group, mirroring how selectedPayPeriod persists
    // the user's choice back to localStorage.
    const [lmType, setLmType] = useState<string>(() => localStorage.getItem('backOfficeSelectedLmType') || 'C');
    const [selectedPayPeriod, setSelectedPayPeriod] = useState<string>(undefined);
    const [selectedOfficeTimeEntry, setSelectedOfficeTimeEntry] = useState<string>(undefined);
    // Single generic sort key/direction (replaces one bool + one handler per column) - mirrors
    // CheckRegisterPage.tsx's sortField/sortDescending + renderSortableHeader pattern, just driving
    // a client-side sort (sortTneChecklist) instead of an OData orderby since this grid's data is
    // already fully loaded.
    const [sortKey, setSortKey] = useState<keyof TimeAndExpenseChecklist | undefined>(undefined);
    const [sortDescending, setSortDescending] = useState<boolean>(false);
    const [officeTimeEntries, setOfficeTimeEntries] = useState<Array<OfficeTimeAndExpenseEntries>>([]);
    const [payPeriodSearchOptions, setPayPeriodSearchOptions] = useState<ODataOptions>(undefined);
    const [fetchNextPayPeriodsPage, setFetchNextPayPeriodsPage] = useState<boolean>(true);
    const [payPeriodSearchTerm, setPayPeriodSearchTerm] = useState<string>('');
    const [timeEntries, setTimeEntries] = useState<Array<TimeAndExpenseChecklist>>([]);
    const [updateTeChecklistResult, setUpdateTeChecklistResult] = useState<UpdateTimeAndExpenseChecklist>(null);
    // Populated by TimeAndExpenseChecklistSearch's Advanced Search slide-out - every field either
    // grid's own columns expose to Enter-Query in the real Oracle Form, confirmed directly against
    // the running app.
    const [advancedCriteria, setAdvancedCriteria] = useState<Omit<ITimeAndExpenseChecklistSearchCriteria, 'periodNo'>>({});
    // Backs the Receipts column's double-click (WHEN-MOUSE-DOUBLECLICK [oid 130] on
    // TE_CHECKLIST_STATS.RECEIPTS in emp_te_checklist.fmt) - reuses the existing, shared
    // ReceiptViewerDialog gallery component as-is.
    const [receiptThumbnails, setReceiptThumbnails] = useState<Array<Receipt>>(undefined);

    const dispatch = useDispatch();
    
    // Initial render
    useEffect(() => {
        onCreate();

        // TODO: *cry* 😭✅
        return () => {
            onDestroy();
        };
    }, []);

    useEffect(() => {
        if (updateTeChecklistResult) {
            alert(`Added ${updateTeChecklistResult.addCount}. Message: ${updateTeChecklistResult.message}`);
        }
    }, [updateTeChecklistResult]);

    useEffect(() => {
        if (!!officeTimeEntries && officeTimeEntries.length > 0) {
            resetOfficeTimeEntries(officeTimeEntries);
            setLoadingOfficeTimeEntrySummary(false);
            setSelectedOfficeTimeEntry(!!officeTimeEntries[0].office ? officeTimeEntries[0].office : 'OK');
        }
    }, [officeTimeEntries]);

    useEffect(() => {
        if (!!timeEntries) {
            resetTimeEntries(officeTimeEntries);
            setLoadingTimeEntries(false);
        }
    }, [timeEntries]);

    useEffect(() => {
        if ((!!payPeriodSearchTerm || payPeriodSearchTerm === '') && !!payPeriodSearchOptions) {
            resetPayPeriodSearchOptions();
        }
    }, [payPeriodSearchTerm]);

    useEffect(() => {
        if (!!selectedPayPeriod && !!selectedOfficeTimeEntry) {
            getTimeAndExpenseEntriesByOffice(Number(selectedPayPeriod), selectedOfficeTimeEntry);
        }
    }, [selectedOfficeTimeEntry]);

    useEffect(() => {
        if (!!selectedPayPeriod && selectedPayPeriod.length > 0) {
            localStorage.setItem('backOfficeSelectedPayPeriod', selectedPayPeriod);
            getOfficeTimeAndExpenseEntries(Number(selectedPayPeriod));
        }
    }, [selectedPayPeriod]);

    const onCreate = () => {
        // Put all initialization code here.
        initializePayPeriodSearchSettings();
    };

    useEffect(() => {
        if (!!payPeriodSearchOptions) {
            getPayPeriods();
        }
    }, [payPeriodSearchOptions]);

    const insertCharAt = (str: string, charToInsert: string, index: number) => {
        return str.slice(0, index) + charToInsert + str.slice(index);
    };

    // Every date column on the staff list was formatting itself via `new Date(value || '').toLocaleDateString()`
    // - when value is null/undefined that's `new Date('')`, an Invalid Date, whose toLocaleDateString()
    // is literally the string "Invalid Date". Only ever showed up for InvoiceDate/CheckDate in practice
    // because those two are the only ones commonly null in real data, not because the other three had
    // different (correct) logic - all five shared this exact bug. Blank is the only string an Invalid
    // Date should ever produce here.
    const formatDateOrBlank = (value: Date | string | null | undefined): string => {
        if (!value) {
            return '';
        }
        const date = new Date(value);
        return isNaN(date.getTime()) ? '' : date.toLocaleDateString();
    };

    // Same formatCurrency EmployeePayrollPage.tsx uses for its own dollar columns.
    const formatCurrency = (value: number) =>
        (value ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

    const expensesTotal = timeEntries.reduce((sum, item) => sum + (!isNaN(Number(item.expenses)) ? Number(item.expenses) : 0), 0);
    const amountTotal = timeEntries.reduce((sum, item) => sum + (!isNaN(Number(item.total)) ? Number(item.total) : 0), 0);
    const daysTotal = timeEntries.reduce((sum, item) => sum + (!isNaN(Number(item.days)) ? Number(item.days) : 0), 0);
    const servicesTotal = timeEntries.reduce((sum, item) => sum + (!isNaN(Number(item.services)) ? Number(item.services) : 0), 0);

    type SortOrder = 'ASC' | 'DESC';

    const sortTneChecklist = <T extends keyof TimeAndExpenseChecklist>(
        array: TimeAndExpenseChecklist[],
        key: T,
        order: SortOrder = 'ASC'
    ): TimeAndExpenseChecklist[] =>
        [...array].sort((a, b): number => {
        const valA = a[key];
        const valB = b[key];
    
        if (valA == null && valB == null) {
            return 0;
        }

        if (valA == null) {
            return order === 'ASC' ? 1 : -1;
        }

        if (valB == null) {
            return order === 'ASC' ? -1 : 1;
        }
    
        return typeof valA === 'string' && typeof valB === 'string'
            ? order === 'ASC'
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA)
            : typeof valA === 'number' && typeof valB === 'number'
            ? order === 'ASC'
            ? valA - valB
            : valB - valA
            : valA instanceof Date && valB instanceof Date
            ? order === 'ASC'
            ? valA.getTime() - valB.getTime()
            : valB.getTime() - valA.getTime()
            : order === 'ASC'
            ? String(valA).localeCompare(String(valB))
            : String(valB).localeCompare(String(valA));
        });

    // Mirrors CheckRegisterPage.tsx's handleSort: clicking the same column again flips direction,
    // clicking a different column starts it ascending.
    const handleSort = (key: keyof TimeAndExpenseChecklist) => {
        setLoadingTimeEntries(true);
        const descending = key === sortKey ? !sortDescending : false;
        setSortKey(key);
        setSortDescending(descending);
        setTimeEntries([...sortTneChecklist(timeEntries, key, descending ? 'DESC' : 'ASC')]);
    };

    // Mirrors CheckRegisterPage.tsx's renderSortableHeader - the whole header cell is the click
    // target, with a small icon previewing the direction the next click will apply.
    const renderSortableHeader = (label: string, key: keyof TimeAndExpenseChecklist, style?: React.CSSProperties) => {
        const willSortDescending = key === sortKey ? !sortDescending : false;
        return (
            <th
                style={{ whiteSpace: 'nowrap', cursor: 'pointer', textAlign: 'center', ...style }}
                title={`Sort ${label} ${willSortDescending ? 'Descending' : 'Ascending'}`}
                onClick={() => handleSort(key)}
            >
                {label}
                <Button
                    style={{ height: '3rem', marginLeft: '.25rem', background: 'transparent' }}
                    text={''}
                    reactIcon={willSortDescending ? FaSortAlphaDown : FaSortAlphaUp}
                    iconHoverColor="blue"
                    iconStyle={{ width: '1.25rem', height: '1.25rem', verticalAlign: 'middle' }}
                />
            </th>
        );
    };

    const resetPayPeriodSearchOptions = () => {
        setFetchNextPayPeriodsPage(true);
        console.log(fetchNextPayPeriodsPage);
        let newPayPeriodSearchOptions = _.cloneDeep(payPeriodSearchOptions);
        newPayPeriodSearchOptions.clearFilters();
        newPayPeriodSearchOptions.addPaging(1, 200);
        const payPeriod = Number(payPeriodSearchTerm.replace('-', ''));
        if (!isNaN(payPeriod)) {
            newPayPeriodSearchOptions.addFilter(new ODataFilter('PERIOD_NO', ODataFilterOperators.Contains, payPeriod));
        }
        setPayPeriodSearchOptions(newPayPeriodSearchOptions);
    };

    const getPayPeriods = async () => {        
        const queryString = payPeriodSearchOptions.toString();
        let result = await get(`/v1.0/AccountsPayable/payperiods${queryString}`);

        if (response.ok) {
            setPayPeriods(result.items.map((pp: PayPeriod) => {
                const insertIndex = pp.periodNumber.toString().length - 2;
                const ppString = insertCharAt(pp.periodNumber.toString(), '-', insertIndex);
                return { id: pp.id, name: ppString, beginDate: pp.beginDate, endDate: pp.endDate };
            }));
        } else {
            console.log('Unable to get pay period data!!!');
        }
    };

    const initializePayPeriodSearchSettings = () => {
        const tableConfig: ITableConfig = {
            rowCount: 500,
            idField: 'Id',
            pageNumber: 1,
            columns: []
        };
        const newPayPeriodSearchOptions = ODataOptions.from(tableConfig);
        newPayPeriodSearchOptions.addOrderBy('Id', ODataOrderByDirection.Descending);
        const storedPayperiod = localStorage.getItem('backOfficeSelectedPayPeriod');
        if (!!storedPayperiod && !isNaN(Number(storedPayperiod))) {
            const dates = getPayPeriodDates(storedPayperiod);
            const beginDate = new Date(dates.beginDate).toLocaleDateString();
            const endDate = new Date(dates.endDate).toLocaleDateString();
            newPayPeriodSearchOptions?.addFilter(new ODataFilter('end_date', ODataFilterOperators.LessThanOrEqual, endDate));
            newPayPeriodSearchOptions?.addFilter(new ODataFilter('beg_date', ODataFilterOperators.GreaterThanOrEqual, beginDate));
            setSelectedPayPeriod(storedPayperiod);
        } else {
            const currentDate = new Date();
            const currentDate2 = new Date();
            const beginDate = new Date(currentDate.setMonth(currentDate.getMonth() - 12)).toLocaleDateString().replace('/', '-').replace('/', '-');
            const endDate = new Date(currentDate2.setMonth(currentDate2.getMonth() + 6)).toLocaleDateString().replace('/', '-').replace('/', '-');
            newPayPeriodSearchOptions?.addFilter(new ODataFilter('end_date', ODataFilterOperators.LessThanOrEqual, endDate));
            newPayPeriodSearchOptions?.addFilter(new ODataFilter('beg_date', ODataFilterOperators.GreaterThanOrEqual, beginDate));
        }
        
        setPayPeriodSearchOptions(newPayPeriodSearchOptions);
    };

    const getPayPeriodDates = (payPeriod: string): { beginDate: Date; endDate: Date } | null => {
        if (!!payPeriod && /^\d{6}$/.test(payPeriod)) {
            const year = parseInt(payPeriod.substring(0, 4), 10);
            const periodNumber = parseInt(payPeriod.substring(4), 10);
    
            // Pay period 1 corresponds to Jan 1–15, 2 is Jan 16–end, 3 is Feb 1–15, etc.
            const month = Math.floor((periodNumber - 1) / 2); // 0-based month
            const isFirstHalf = (periodNumber % 2) === 1;
    
            let beginDate: Date;
            let endDate: Date;
    
            if (isFirstHalf) {
                beginDate = new Date(year, month, 1);
                endDate = new Date(year, month, 15);
            } else {
                beginDate = new Date(year, month, 16);
                endDate = new Date(year, month + 2, 0); // Last day of the month
            }
    
            return { beginDate, endDate };
        } else {
            return null;
        }
    };    

    const onDestroy = () => {
        // Put all destruction code here.
    };

    const displayHeading2 = () => {
        const title = 'Offices';

        return title;
    };

    const displayHeading3 = () => {
        const title = 'Time Entries';

        return title;
    };

    // Flattens the Advanced Search criteria into query-string params matching
    // TimeAndExpenseChecklistSearchCriteria's own property names 1:1 (ASP.NET model-binds a
    // [FromQuery] object from top-level query params of the same name).
    const buildAdvancedSearchQuery = (criteria: Omit<ITimeAndExpenseChecklistSearchCriteria, 'periodNo'>): string => {
        const query = new URLSearchParams();
        Object.entries(criteria).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                query.set(key, String(value));
            }
        });
        return query.toString();
    };

    const getOfficeTimeAndExpenseEntries = async (payPeriodNumber: number) => {
        setLoadingOfficeTimeEntrySummary(true);
        const advancedQuery = buildAdvancedSearchQuery(advancedCriteria);
        let result = await get(`/v1.0/AccountsPayable/timeandexpense/timeentries/payperiod/${payPeriodNumber}?lmType=${lmType}${advancedQuery ? `&${advancedQuery}` : ''}`);

        if (response.ok) {
            setOfficeTimeEntries(result);
        } else {
            console.log('Unable to get office time entry Data!');
        }
    };

    const getTimeAndExpenseEntriesByOffice = async (payPeriodNumber: number, office: string) => {
        setLoadingTimeEntries(true);
        const advancedQuery = buildAdvancedSearchQuery(advancedCriteria);
        let result = await get(`/v1.0/AccountsPayable/timeandexpense/timeentries/payperiod/${payPeriodNumber}/office/${office}?lmType=${lmType}${advancedQuery ? `&${advancedQuery}` : ''}`);

        if (response.ok) {
            setTimeEntries(result);
        } else {
            console.log('Unable to get time entry Data!');
        }
    };

    // Advanced Search's own Search button - re-runs both grids' fetches with the new criteria for
    // whichever period/office are already selected.
    const handleAdvancedSearch = (criteria: Omit<ITimeAndExpenseChecklistSearchCriteria, 'periodNo'>) => {
        setAdvancedCriteria(criteria);
    };

    useEffect(() => {
        if (!!selectedPayPeriod) {
            getOfficeTimeAndExpenseEntries(Number(selectedPayPeriod));
            if (!!selectedOfficeTimeEntry) {
                getTimeAndExpenseEntriesByOffice(Number(selectedPayPeriod), selectedOfficeTimeEntry);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [advancedCriteria, lmType]);

    // Employee Type radio group - persists the choice the same way selectedPayPeriod does, so a
    // manual switch survives a refresh just like the period selection does.
    const handleLmTypeChanged = (value: string) => {
        localStorage.setItem('backOfficeSelectedLmType', value);
        setLmType(value);
    };

    // Backs Add_New_LM's own action (WHEN-BUTTON-PRESSED [oid 72] on B_ADD) - Oracle itself never
    // re-queries TECO/Te_CheckList_Office after inserting (just alerts the count), but a newly added
    // landman not showing up without a manual re-search is worse UX with nothing gained by matching
    // that part faithfully, so this deliberately re-fetches both grids afterward instead.
    const updateTimeAndExpenseChecklist = async (payPeriodNumber: number) => {
        let result = await post(`/v1.0/AccountsReceivable/timeExpense/checklist/payPeriod/${payPeriodNumber}/update`);

        if (response.ok) {
            setUpdateTeChecklistResult(result);
            getOfficeTimeAndExpenseEntries(payPeriodNumber);
            if (!!selectedOfficeTimeEntry) {
                getTimeAndExpenseEntriesByOffice(payPeriodNumber, selectedOfficeTimeEntry);
            }
        } else {
            console.log('Unable to update time and expense checklist!');
        }
    };

    // Mirrors Add_New_LM's own null-period guard (WHEN-BUTTON-PRESSED [oid 72] on B_ADD) - Oracle
    // shows this exact alert instead of running the insert when no period is loaded yet.
    const addNewlyAddedLandmen = () => {
        if (!selectedPayPeriod) {
            alert('A pay period number must be entered first before a new landman can be added to the list.');
            return;
        }
        updateTimeAndExpenseChecklist(Number(selectedPayPeriod));
    };

    // Mirrors emp_te_checklist.fmt's drag-a-landman-onto-an-office interaction (WHEN-MOUSE-UP on the
    // TECO block -> Move_LM_Office): dropping a staff-list row onto an Offices-panel row reassigns
    // that landman there for this period forward. Uses react-beautiful-dnd, matching the drag pattern
    // already established in HorizonSequencingComponent.tsx rather than hand-rolled HTML5 drag events.
    const handleDragEnd = async (result: DropResult) => {
        if (!result.destination || !result.destination.droppableId.startsWith('office-')) {
            return;
        }

        const office = result.destination.droppableId.replace('office-', '');
        const tsdNo = Number(result.draggableId);

        if (!tsdNo || !selectedPayPeriod) {
            return;
        }

        await put(`/v1.0/AccountsPayable/timeandexpense/moveoffice/payperiod/${selectedPayPeriod}/tsd/${tsdNo}/office/${office}`);

        if (response.ok) {
            getOfficeTimeAndExpenseEntries(Number(selectedPayPeriod));
            if (!!selectedOfficeTimeEntry) {
                getTimeAndExpenseEntriesByOffice(Number(selectedPayPeriod), selectedOfficeTimeEntry);
            }
        } else {
            alert('Unable to move this landman to that office. Please try again.');
        }
    };

    const displayOfficeExpenseSummary = () => {
        return (
            <table className={'project-billing'}>
                <thead className={'project-billing'}>
                    <th>Office</th>
                    <th>Type</th>
                    <th>LM #</th>
                    <th>Entered</th>
                    <th>Invoice</th>
                    <th>Proof</th>
                    <th>Paid</th>
                </thead>
                <tbody className={'project-billing'}>
                {!loadingOfficeTimeEntrySummary && !!officeTimeEntries && officeTimeEntries?.length > 0 && officeTimeEntries?.map(
                    (lp: OfficeTimeAndExpenseEntries, index: number) => {
                    return (
                        <Droppable droppableId={`office-${lp?.office}`} key={index}>
                        {(dropProvided, dropSnapshot) => (
                        <tr
                            ref={dropProvided.innerRef}
                            {...dropProvided.droppableProps}
                            tabIndex={0}
                            id={'leftPanelRow' + index}
                            className="table-item-hover"
                            style={{ textAlign: 'center', ...(dropSnapshot.isDraggingOver && { backgroundColor: '#c8d9e9' }) }}
                            onClick={() => setSelectedOfficeTimeEntry(lp?.office)}
                        >
                            <td style={{ width: '5%' }}>
                                <RHFInputBox
                                    name={`officeTimeAndExpensesEntries[${index}].office`}
                                    readOnly={true}
                                    type="text"
                                    defaultVal={lp?.office}
                                    register={registerOfficeTimeEntries}
                                    errors={officeTimeEntriesErrors}
                                    style={{
                                        width: '100%',
                                        ...(lp?.office === selectedOfficeTimeEntry && { backgroundColor: 'yellow' })
                                    }}
                                    bodyStyle={{ width: '100%' }}
                                />
                            </td>
                            <td style={{ width: '4%' }}>
                                <RHFInputBox
                                    name={`officeTimeAndExpensesEntries[${index}].type`}
                                    readOnly={true}
                                    type="text"
                                    defaultVal={lp?.landmenType?.toString()}
                                    register={registerOfficeTimeEntries}
                                    errors={officeTimeEntriesErrors}
                                    style={{
                                        width: '100%',
                                        ...(lp?.office === selectedOfficeTimeEntry && { backgroundColor: 'yellow' })
                                    }}
                                    bodyStyle={{ width: '100%' }}
                                />
                            </td>
                            <td style={{ width: '6%' }}>
                                <RHFInputBox
                                    name={`officeTimeAndExpensesEntries[${index}].landmenCount`}
                                    readOnly={true}
                                    type="number"
                                    defaultVal={lp?.landmenCount?.toString()}
                                    register={registerOfficeTimeEntries}
                                    errors={officeTimeEntriesErrors}
                                    style={{
                                        width: '100%',
                                        ...(lp?.office === selectedOfficeTimeEntry && { backgroundColor: 'yellow' })
                                    }}
                                    bodyStyle={{ width: '100%' }}
                                />
                            </td>
                            <td style={{ width: '6%' }}>
                                <RHFInputBox
                                    name={`officeTimeAndExpensesEntries[${index}].entered`}
                                    readOnly={true}
                                    type="number"
                                    defaultVal={lp?.entered?.toString()}
                                    register={registerOfficeTimeEntries}
                                    errors={officeTimeEntriesErrors}
                                    style={{
                                        width: '100%',
                                        ...(lp?.office === selectedOfficeTimeEntry && { backgroundColor: 'yellow' })
                                    }}
                                    bodyStyle={{ width: '100%' }}
                                />
                            </td>
                            <td style={{ width: '6%' }}>
                                <RHFInputBox
                                    name={`officeTimeAndExpensesEntries[${index}].invoiced`}
                                    readOnly={true}
                                    type="number"
                                    defaultVal={lp?.invoiced?.toString()}
                                    register={registerOfficeTimeEntries}
                                    errors={officeTimeEntriesErrors}
                                    style={{
                                        width: '100%',
                                        ...(lp?.office === selectedOfficeTimeEntry && { backgroundColor: 'yellow' })
                                    }}
                                    bodyStyle={{ width: '100%' }}
                                />
                            </td>
                            <td style={{ width: '6%' }}>
                                <RHFInputBox
                                    name={`officeTimeAndExpensesEntries[${index}].proof`}
                                    readOnly={true}
                                    type="number"
                                    defaultVal={lp?.proof?.toString()}
                                    register={registerOfficeTimeEntries}
                                    errors={officeTimeEntriesErrors}
                                    style={{
                                        width: '100%',
                                        ...(lp?.office === selectedOfficeTimeEntry && { backgroundColor: 'yellow' })
                                    }}
                                    bodyStyle={{ width: '100%' }}
                                />
                            </td>
                            <td style={{ width: '6%' }}>
                                <RHFInputBox
                                    name={`officeTimeAndExpensesEntries[${index}].paid`}
                                    readOnly={true}
                                    type="number"
                                    defaultVal={lp?.paid?.toString()}
                                    register={registerOfficeTimeEntries}
                                    errors={officeTimeEntriesErrors}
                                    style={{
                                        width: '100%',
                                        ...(lp?.office === selectedOfficeTimeEntry && { backgroundColor: 'yellow' })
                                    }}
                                    bodyStyle={{ width: '100%' }}
                                />
                            </td>
                            <td style={{ display: 'none' }}>{dropProvided.placeholder}</td>
                        </tr>
                        )}
                        </Droppable>
                    );
                })}
                </tbody>
            </table>
        );
    };

    const onSubmit = () => {
        console.log('onSubmit called for save project');
    };

    const setSelectedPayPeriodValue = (value: string) => {
        if (!value) {
            setSelectedPayPeriod(undefined);
            return;
        }
        if (Array.isArray(payPeriods)) {
            const labelValue: PayPeriod = payPeriods.find((pp) =>
                pp.id.toString() === value
            );
            if (!!labelValue?.name) {
                setSelectedPayPeriod(labelValue.name.toString().replace('-', ''));
            }
        }
    };

    const displayTitle = () => {
        const title = 'Time & Expense Checklist';

        return title;
    };

    const navigateToTimeExpenseEntry = (employeeNumber: number) => {
        localStorage.setItem('backOfficeSelectedLandman', employeeNumber?.toString());
        dispatch(push(`/project-billing/timeexpense`));
    };

    // Mirrors WHEN-MOUSE-DOUBLECLICK [oid 130] on TE_CHECKLIST_STATS.RECEIPTS - only opens the
    // viewer if this row actually has receipts, matching Oracle's own `receipts > 0` guard.
    const openReceiptsViewer = async (employeeNumber: number, receiptCount: number) => {
        if (!receiptCount || receiptCount <= 0 || !selectedPayPeriod) {
            return;
        }
        let result = await get(`/v1.0/AccountsPayable/timeandexpense/receipts/tsd/${employeeNumber}/payperiod/${Number(selectedPayPeriod)}/thumbnails`);

        if (response.ok) {
            setReceiptThumbnails(Array.isArray(result) && result.length > 0 ? result : undefined);
            dispatch(actions.layout.showDialog(DialogList.RECEIPT_VIEWER));
        } else {
            console.log('Unable to get landman expense receipt thumbnail data!');
        }
    };

    const hideReceiptsViewer = () => {
        dispatch(actions.layout.hideDialog());
        setReceiptThumbnails(undefined);
    };

    // One cell per staff-list column, same widths as the data row above, so the totals actually
    // land under the columns they total (this previously had 20 cells against the data row's 17,
    // silently misaligning everything from Receipts onward).
    const displayTotals = () => {
        const totalLabelStyle: React.CSSProperties = {
            display: 'block', width: '100%', height: '2.75rem', lineHeight: '2.75rem',
            border: 'solid black 1.5px', backgroundColor: '#6DEEF7', textAlign: 'center',
        };

        return (
            <tr style={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                <td style={{ width: '5%' }}/>
                <td style={{ width: '7%' }}/>
                <td style={{ width: '3%' }}/>
                <td style={{ width: '3%' }}/>
                <td style={{ width: '3%' }}/>
                <td style={{ width: '3%' }}/>
                <td style={{ width: '3%' }}/>
                <td style={{ width: '7%' }}/>
                <td style={{ width: '7%' }}/>
                <td style={{ width: '7%' }}/>
                <td style={{ width: '7%' }}/>
                <td style={{ width: '7%' }}/>
                <td style={{ width: '4%', border: '0', textAlign: 'right', paddingRight: '.5rem', fontSize: '1.2rem', fontWeight: 'bold' }}>Totals:</td>
                <td style={{ width: '5%' }}>
                    <label style={totalLabelStyle} id={'daysTotal'}>{daysTotal.toFixed(4)}</label>
                </td>
                <td style={{ width: '6%' }}>
                    <label style={totalLabelStyle} id={'servicesTotal'}>{formatCurrency(servicesTotal)}</label>
                </td>
                <td style={{ width: '6%' }}>
                    <label style={totalLabelStyle} id={'expensesTotal'}>{formatCurrency(expensesTotal)}</label>
                </td>
                <td style={{ width: '6%' }}>
                    <label style={totalLabelStyle} id={'grandTotal'}>{formatCurrency(amountTotal)}</label>
                </td>
            </tr>
        );
    };

    const displayStaffList = () => {
        return <div>
            <table className={'project-billing'} style={{ width: '100%', tableLayout: 'fixed' }}>
                <thead>
                    <tr style={{whiteSpace: 'nowrap', textAlign: 'left' }}>
                        {renderSortableHeader('Emp #', 'employeeNumber', { width: '5%' })}
                        {renderSortableHeader('Name', 'name', { width: '7%' })}
                        {renderSortableHeader('Active', 'active', { width: '3%' })}
                        {renderSortableHeader('TkPrs', 'turnkeyProspects', { width: '3%' })}
                        {renderSortableHeader('Aprs', 'approvalCount', { width: '3%' })}
                        {renderSortableHeader('Aprd', 'approvedCount', { width: '3%' })}
                        {renderSortableHeader('NApr', 'approvalsNeeded', { width: '3%' })}
                        {renderSortableHeader('Last Date', 'lastDate', { width: '7%' })}
                        {renderSortableHeader('Inv. Date', 'invoiceDate', { width: '7%' })}
                        {renderSortableHeader('Proof Date', 'proofDate', { width: '7%' })}
                        {renderSortableHeader('File Date', 'fileDate', { width: '7%' })}
                        {renderSortableHeader('Check Date', 'checkDate', { width: '7%' })}
                        {renderSortableHeader('receipts', 'receipts', { width: '4%' })}
                        {renderSortableHeader('Days', 'days', { width: '5%' })}
                        {renderSortableHeader('Services', 'services', { width: '6%' })}
                        {renderSortableHeader('Expenses', 'expenses', { width: '6%' })}
                        {renderSortableHeader('Total', 'total', { width: '6%' })}
                    </tr>
                </thead>
                <Droppable droppableId="staffList" isDropDisabled={true}>
                {(listDropProvided) => (
                <tbody className={'project-billing'} ref={listDropProvided.innerRef} {...listDropProvided.droppableProps}>
                    {!loadingTimeEntries && !!timeEntries && timeEntries.length > 0 && timeEntries?.map(
                        (rpc: TimeAndExpenseChecklist, index: number) => {
                        return (
                            <Draggable key={rpc?.id + index} draggableId={String(rpc.employeeNumber)} index={index}>
                            {(dragProvided, dragSnapshot) => (
                            <tr
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                onDoubleClick={() => navigateToTimeExpenseEntry(rpc.employeeNumber)}
                                className="table-item-hover"
                                style={{ ...dragProvided.draggableProps.style, textAlign: 'center', ...(dragSnapshot.isDragging && { backgroundColor: '#c8d9e9' }) }}
                            >
                                <td style={{ width: '5%' }}>
                                    <RHFInputBox
                                        name={`landmanTsdNumber`}
                                        type="number"
                                        defaultVal={rpc?.employeeNumber?.toString()}
                                        register={registerTimeEntries}
                                        errors={timeEntriesErrors}
                                        style={{ width: '100%', cursor: 'grab', textAlign: 'center' }}
                                        bodyStyle={{ width: '100%' }}
                                        readOnly={true}
                                    />
                                </td>
                                <td style={{ width: '7%' }}>
                                    <RHFInputBox
                                        name={`employeeName`}
                                        type="text"
                                        defaultVal={rpc?.name}
                                        register={registerTimeEntries}
                                        errors={timeEntriesErrors}
                                        style={{ width: '100%', background: '#d5f7f5', cursor: 'grab' }}
                                        bodyStyle={{ width: '100%' }}
                                        readOnly={true}
                                    />
                                </td>
                                <td style={{ width: '3%' }}>
                                    <RHFInputBox
                                        name={`active`}
                                        type="text"
                                        defaultVal={!!rpc?.active ? 'Y' : 'N'}
                                        register={registerTimeEntries}
                                        errors={timeEntriesErrors}
                                        style={{ width: '100%', cursor: 'grab' }}
                                        bodyStyle={{ width: '100%' }}
                                        readOnly={true}
                                    />
                                </td>
                                <td style={{  width: '3%' }}>
                                    <RHFInputBox
                                        name={`tkprs`}
                                        defaultVal={rpc?.turnkeyProspects?.toString()}
                                        style={{ width: '100%', cursor: 'grab' }}
                                        bodyStyle={{ width: '100%'}}
                                        register={registerTimeEntries}
                                        errors={timeEntriesErrors}
                                        readOnly={true}
                                    />
                                </td>
                                <td style={{ width: '3%' }}>
                                    <RHFInputBox
                                        name={`aprs`}
                                        defaultVal={!!rpc?.approvalCount ? rpc.approvalCount.toString() : '0'}
                                        style={{ width: '100%', cursor: 'grab' }}
                                        bodyStyle={{ width: '100%'}}
                                        register={registerTimeEntries}
                                        errors={timeEntriesErrors}
                                        readOnly={true}
                                    />
                                </td>
                                <td style={{ width: '3%' }}>
                                    <RHFInputBox
                                        name={`aprd`}
                                        defaultVal={!!rpc?.approvedCount ? rpc.approvedCount.toString() : '0'}
                                        style={{ width: '100%', cursor: 'grab' }}
                                        bodyStyle={{ width: '100%'}}
                                        register={registerTimeEntries}
                                        errors={timeEntriesErrors}
                                        readOnly={true}
                                    />
                                </td>
                                <td style={{ width: '3%' }}>
                                    <RHFInputBox
                                        name={`napr`}
                                        defaultVal={!!rpc?.approvalsNeeded ? rpc.approvalsNeeded.toString() : '0'}
                                        style={{ width: '100%', cursor: 'grab' }}
                                        bodyStyle={{ width: '100%'}}
                                        register={registerTimeEntries}
                                        errors={timeEntriesErrors}
                                        readOnly={true}
                                    />
                                </td>
                                <td style={{ width: '7%' }}>
                                    <RHFInputBox
                                        name={`lastDate`}
                                        defaultVal={formatDateOrBlank(rpc?.lastDate)}
                                        style={{ width: '100%', cursor: 'grab' }}
                                        bodyStyle={{ width: '100%'}}
                                        register={registerTimeEntries}
                                        errors={timeEntriesErrors}
                                        readOnly={true}
                                    />
                                </td>
                                <td style={{ width: '7%' }}>
                                    <RHFInputBox
                                        name={`invoiceDate`}
                                        defaultVal={formatDateOrBlank(rpc?.invoiceDate)}
                                        style={{ width: '100%', cursor: 'grab' }}
                                        bodyStyle={{ width: '100%'}}
                                        register={registerTimeEntries}
                                        errors={timeEntriesErrors}
                                        readOnly={true}
                                    />
                                </td>
                                <td style={{ width: '7%' }}>
                                    <RHFInputBox
                                        name={`proofDate`}
                                        defaultVal={formatDateOrBlank(rpc?.proofDate)}
                                        style={{ width: '100%', cursor: 'grab' }}
                                        bodyStyle={{ width: '100%'}}
                                        register={registerTimeEntries}
                                        errors={timeEntriesErrors}
                                        readOnly={true}
                                    />
                                </td>
                                <td style={{ width: '7%' }}>
                                    <RHFInputBox
                                        name={`tsFileDate`}
                                        defaultVal={formatDateOrBlank(rpc?.fileDate)}
                                        style={{ width: '100%', cursor: 'grab' }}
                                        bodyStyle={{ width: '100%'}}
                                        register={registerTimeEntries}
                                        errors={timeEntriesErrors}
                                        readOnly={true}
                                    />
                                </td>
                                <td style={{ width: '7%' }}>
                                    <RHFInputBox
                                        name={`checkDate`}
                                        defaultVal={formatDateOrBlank(rpc?.checkDate)}
                                        style={{ width: '100%', cursor: 'grab' }}
                                        bodyStyle={{ width: '100%'}}
                                        register={registerTimeEntries}
                                        errors={timeEntriesErrors}
                                        readOnly={true}
                                    />
                                </td>
                                <td
                                    style={{ width: '4%' }}
                                    onDoubleClick={(e) => {
                                        e.stopPropagation();
                                        openReceiptsViewer(rpc.employeeNumber, rpc.receipts);
                                    }}
                                >
                                    <RHFInputBox
                                        name={`receipts`}
                                        defaultVal={!!rpc?.receipts ? rpc.receipts.toString() : '0'}
                                        style={{ width: '100%', cursor: 'grab' }}
                                        bodyStyle={{ width: '100%'}}
                                        register={registerTimeEntries}
                                        errors={timeEntriesErrors}
                                        readOnly={true}
                                    />
                                </td>
                                <td style={{ width: '5%' }}>
                                    <RHFInputBox
                                        name={`days`}
                                        type="number"
                                        defaultVal={rpc?.days?.toString()}
                                        register={registerTimeEntries}
                                        errors={timeEntriesErrors}
                                        style={{ width: '100%', cursor: 'grab' }}
                                        bodyStyle={{ width: '100%' }}
                                        readOnly={true}
                                    />
                                </td>
                                <td style={{ width: '6%' }}>
                                    <RHFInputBox
                                        name={`services`}
                                        defaultVal={formatCurrency(rpc?.services)}
                                        style={{ width: '100%', cursor: 'grab', textAlign: 'right' }}
                                        bodyStyle={{ width: '100%'}}
                                        register={registerTimeEntries}
                                        errors={timeEntriesErrors}
                                        readOnly={true}
                                    />
                                </td>
                                <td style={{ width: '6%' }}>
                                    <RHFInputBox
                                        name={`expenses`}
                                        defaultVal={formatCurrency(rpc?.expenses)}
                                        style={{ width: '100%', cursor: 'grab', textAlign: 'right' }}
                                        bodyStyle={{ width: '100%'}}
                                        register={registerTimeEntries}
                                        errors={timeEntriesErrors}
                                        readOnly={true}
                                    />
                                </td>
                                <td style={{ width: '6%' }}>
                                    <RHFInputBox
                                        name={`totalAmount`}
                                        defaultVal={formatCurrency(rpc?.total)}
                                        style={{ width: '100%', cursor: 'grab', textAlign: 'right' }}
                                        bodyStyle={{ width: '100%'}}
                                        register={registerTimeEntries}
                                        errors={timeEntriesErrors}
                                        readOnly={true}
                                    />
                                </td>
                            </tr>
                            )}
                            </Draggable>
                        );
                    })}
                    <tr style={{ display: 'none' }}><td>{listDropProvided.placeholder}</td></tr>
                </tbody>
                )}
                </Droppable>
                <tfoot>
                    {displayTotals()}
                </tfoot>
            </table>
        </div>;
    };

    return (
        <>
        <div>
        <PBNavBar/>
            <TimeAndExpenseChecklistSearch
                selectedPayperiod={selectedPayPeriod}
                setSelectedPayPeriod={setSelectedPayPeriodValue}
                setPayPeriodSearchTerm={setPayPeriodSearchTerm}
                payPeriods={!!payPeriods && payPeriods.length > 0 ?
                    payPeriods?.map((pp) => { return {id: pp?.id, name: !!pp?.name ? pp?.name : ''}; }) : []}
                onAdvancedSearch={handleAdvancedSearch}
                employeeType={lmType}
                setEmployeeType={handleLmTypeChanged}
            />
           <div id={'TimeAndExpenseChecklistPage'} style={{margin: '0 auto', display: 'flex', maxWidth: '98vw', justifyContent: 'center'}}>
                <form id={`ProjectMaintenanceForm`} onSubmit={onSubmit}>
                    <Page className="dlc-maxer-width">                    
                        <Page.Section width={14}>
                            <Panel backgroundColor="#D3D3D3">
                                <Panel centerPanelTitle={true} panelTitle={displayTitle()}>
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                    <Button
                                        style={{ height: '3rem', background: 'transparent' }}
                                        text={''}
                                        title="Add Recent Time Entry"
                                        reactIcon={MdAccessTime}
                                        iconHoverColor="blue"
                                        iconStyle={{ width: '2.5rem', height: '2.5rem', verticalAlign: 'center' }}
                                        onClick={() => addNewlyAddedLandmen()}
                                    />
                                </div>
                            </Panel>
                            </Panel>
                        </Page.Section>
                    </Page>
                    <Page className="dlc-maxer-width">
                        <DragDropContext onDragEnd={handleDragEnd}>
                        <Page.Section width={3}>
                            <Panel backgroundColor="#D3D3D3">
                                <Panel panelTitle={displayHeading2()}>
                                    <div style={{display: 'flex', width: '100%', border: 'solid rgb(99, 147, 193) 1px', paddingBottom: '.5rem' }}>
                                        {!!loadingOfficeTimeEntrySummary && (
                                            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                                <span>Retreiving Office Time Entry Summaries...</span>
                                                <LoadingSpinner />
                                            </div>
                                        )}
                                        {!loadingOfficeTimeEntrySummary && displayOfficeExpenseSummary()}
                                    </div>
                                </Panel>
                            </Panel>
                        </Page.Section>
                        <Page.Section width={10}>
                            <Panel backgroundColor="#D3D3D3">
                                <Panel panelTitle={displayHeading3()}>
                                    <div style={{ border: 'solid rgb(99, 147, 193) 1px', paddingBottom: '.5rem' }}>
                                        {!!loadingTimeEntries && (
                                            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                                <span>Retreiving Time Entries...</span>
                                                <LoadingSpinner />
                                            </div>
                                        )}
                                        {!loadingTimeEntries && displayStaffList()}
                                    </div>
                                </Panel>
                            </Panel>
                        </Page.Section>
                        </DragDropContext>
                    </Page>
                </form>
            </div>
            <ReceiptViewerDialog
                context={1}
                dialogName={DialogList.RECEIPT_VIEWER}
                handleConfirm={hideReceiptsViewer}
                hideDialog={hideReceiptsViewer}
                receipts={receiptThumbnails}
                sourceType={DocumentSourceType.TimeExpenseReceipt}
            />
        </div>
        </>
    );
};

export default TimeAndExpenseChecklistPage;



// WEBPACK FOOTER //
// ./src/components/project-billing/pages/TimeAndExpenseChecklistPage.tsx